/** Represents a valid spacing amount within the design system. */
export type Spacing = 0 | 2 | 4 | 8 | 12 | 16 | 24 | 32 | 40 | 48 | 64 | 96;

/** Represents a screen size breakpoint for responsive design. */
export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

/** Represents a primitive value that can change based on screen size. */
export type Responsive<T extends string | number | boolean> = T | Partial<Record<Breakpoint, T>>;

/**
 * Given params like so:
 *
 * ```ts
 * (
 *   'box-ml',
 *   'space',
 *   {
 *     sm: 4,
 *     lg: 6,
 *   }
 * )
 * ```
 *
 * Creates an object like so:
 *
 * ```ts
 * {
 *   '--cim-box-ml-sm': 'var(--cim-space-4)',
 *   '--cim-box-ml-lg': 'var(--cim-space-6)',
 * }
 * ```
 *
 */
export function getResponsiveVariables<T extends string | number | boolean>(
  targetTokenGroup: string,
  sourceTokenGroup: string,
  data?: Responsive<T>,
  defaultValue?: T,
): Record<string, string> | undefined {
  if (data === undefined && defaultValue === undefined) {
    return undefined;
  }

  if (typeof data !== 'object') {
    return {
      [`--cim-${targetTokenGroup}-sm`]: `var(--cim-${sourceTokenGroup}-${data ?? defaultValue})`,
    };
  }

  const result: Record<string, string> =
    defaultValue !== undefined
      ? {
          // Default value in case the smallest breakpoint isn't defined
          [`--cim-${targetTokenGroup}-sm`]: `var(--cim-${sourceTokenGroup}-${defaultValue})`,
        }
      : {};

  Object.entries(data).forEach(([breakpoint, value]) => {
    result[`--cim-${targetTokenGroup}-${breakpoint}`] = `var(--cim-${sourceTokenGroup}-${value})`;
  });

  return result;
}
